import React, { useEffect, useRef, useState } from 'react'
import { useChecklistStore, ChecklistItem } from '../store/useChecklistStore'
import { Plus, Trash2, Edit2, Check, X, GripVertical } from 'lucide-react'

const EMOJI_PALETTE = [
  '🦺', '🔺', '📄', '🛞', '🔧', '🌡️', '🔑', '⚡', '🩹', '🧯', '🔦', '📦', '🪜', '🧹', '🧤'
]

function slugify(str: string) {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export function ChecklistManager() {
  const {
    checklistItems,
    itemsLoading,
    itemsError,
    fetchItems,
    addItem,
    updateItem,
    removeItem,
    reorderItems,
  } = useChecklistStore()

  const [editingId, setEditingId] = useState<string | null>(null)
  
  // State for adding new item
  const [newLabel, setNewLabel] = useState('')
  const [newQuestion, setNewQuestion] = useState('')
  const [newIcon, setNewIcon] = useState('🦺')
  const [newCategories, setNewCategories] = useState('')
  const [newIsBase, setNewIsBase] = useState(true)
  const [showNewPalette, setShowNewPalette] = useState(false)

  // State for editing item
  const [editForm, setEditForm] = useState<Partial<ChecklistItem>>({})
  const [showEditPalette, setShowEditPalette] = useState(false)

  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  useEffect(() => { fetchItems() }, [fetchItems])

  /* ── Drag & Drop ──────────────────────────────────────────────────────── */
  const handleDragStart = (index: number) => { dragItem.current = index }
  const handleDragEnter = (index: number) => { dragOverItem.current = index }
  const handleDragEnd   = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    const reordered = [...checklistItems]
    const [moved]   = reordered.splice(dragItem.current, 1)
    reordered.splice(dragOverItem.current, 0, moved)
    dragItem.current     = null
    dragOverItem.current = null
    reorderItems(reordered)
  }

  /* ── Actions ───────────────────────────────────────────────────────────── */
  const handleAdd = () => {
    if (!newLabel.trim()) return
    addItem({
      id:             slugify(newLabel),
      label:          newLabel.trim(),
      icon:           newIcon,
      question:       newQuestion.trim() || `Avez-vous vérifié la présence de : ${newLabel.trim()} ?`,
      category_codes: newCategories.split(',').map(c => c.trim().toUpperCase()).filter(Boolean),
      is_base:        newIsBase
    })
    setNewLabel('')
    setNewQuestion('')
    setNewCategories('')
    setNewIsBase(true)
  }

  const startEdit = (item: ChecklistItem) => {
    setEditingId(item.id)
    setEditForm({ ...item, category_codes_str: item.category_codes.join(', ') })
  }

  const saveEdit = () => {
    if (!editingId) return
    const catsStr = (editForm as any).category_codes_str || ''
    const cats = catsStr.split(',').map((c: string) => c.trim().toUpperCase()).filter(Boolean)
    
    updateItem(editingId, {
      ...editForm,
      category_codes: cats,
      category_codes_str: undefined // remove temporary helper field
    } as any)
    setEditingId(null)
  }

  /* ── Rendu ────────────────────────────────────────────────────────────── */
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          📋 Checklist Sécurité
          {itemsLoading && <span className="animate-spin text-blue-500 font-normal">🔄</span>}
        </h2>
        <button onClick={() => fetchItems()} className="p-2 transition-all hover:bg-gray-100 rounded-lg">
          🔄
        </button>
      </div>

      {itemsError && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm italic">
          ⚠️ {itemsError} — Fallback items actifs
        </div>
      )}

      {/* ── Formulaire Ajout (Sticky style) ── */}
      <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-4 shadow-sm">
        <h3 className="text-blue-800 font-bold mb-3 flex items-center gap-2">
          <Plus size={18} /> Ajouter un équipement
        </h3>
        <div className="grid grid-cols-1 gap-3">
          <div className="flex gap-2">
             {/* Icon Picker */}
             <div className="relative">
              <button
                onClick={() => setShowNewPalette(!showNewPalette)}
                className="w-12 h-12 flex items-center justify-center bg-white border-2 border-blue-200 rounded-xl text-2xl"
              >
                {newIcon}
              </button>
              {showNewPalette && (
                <div className="absolute top-14 left-0 z-[9999] w-64 bg-white border shadow-2xl rounded-xl p-2 flex flex-wrap gap-1">
                  {EMOJI_PALETTE.map(e => (
                    <button key={e} onClick={() => { setNewIcon(e); setShowNewPalette(false) }} className="w-10 h-10 flex items-center justify-center text-xl hover:bg-gray-100 rounded">
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              placeholder="Libellé (ex: Gilet)"
              className="flex-1 bg-white border-2 border-blue-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
            />
          </div>
          <input
            placeholder="Question (Optionnel)"
            className="w-full bg-white border-2 border-blue-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500"
            value={newQuestion}
            onChange={e => setNewQuestion(e.target.value)}
          />
          <div className="flex items-center gap-4 px-1">
             <label className="flex items-center gap-2 cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  checked={newIsBase} 
                  onChange={e => setNewIsBase(e.target.checked)} 
                  className="w-4 h-4 rounded text-blue-600"
                />
                <span className="text-sm font-semibold text-blue-800">Toutes catégories (Base)</span>
             </label>
          </div>
          {!newIsBase && (
            <input
              placeholder="Codes catégories (ex: 03F, 06F, RF)"
              className="w-full bg-white border-2 border-blue-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500 animate-in fade-in"
              value={newCategories}
              onChange={e => setNewCategories(e.target.value)}
            />
          )}
          <button 
            onClick={handleAdd}
            disabled={!newLabel.trim()}
            className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            Ajouter à la liste
          </button>
        </div>
      </div>

      {/* ── Liste des items ── */}
      <div className="flex flex-col gap-3 mt-4">
        {checklistItems.map((item, index) => {
          const isEditing = editingId === item.id
          return (
            <div
              key={item.id}
              draggable={!isEditing}
              onDragStart={() => handleDragStart(index)}
              onDragEnter={() => handleDragEnter(index)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
              className={`bg-white border rounded-2xl p-3 shadow-sm flex items-start gap-3 transition-all ${
                isEditing ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              <GripVertical className="text-gray-300 mt-2 flex-shrink-0 cursor-grab" size={18} />
              
              {/* Icon / Edit Icon */}
              <div className="relative flex-shrink-0">
                {isEditing ? (
                   <button
                    onClick={() => setShowEditPalette(!showEditPalette)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-50 border rounded-lg text-xl"
                  >
                    {editForm.icon}
                  </button>
                ) : (
                  <span className="w-10 h-10 flex items-center justify-center text-xl">
                    {item.icon}
                  </span>
                )}
                {showEditPalette && (
                  <div className="absolute top-12 left-0 z-[9999] w-64 bg-white border shadow-2xl rounded-xl p-2 flex flex-wrap gap-1">
                    {EMOJI_PALETTE.map(e => (
                      <button key={e} onClick={() => { setEditForm({...editForm, icon: e}); setShowEditPalette(false) }} className="w-10 h-10 flex items-center justify-center text-xl hover:bg-gray-100 rounded">
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="flex flex-col gap-2">
                    <input 
                      value={editForm.label} 
                      onChange={e => setEditForm({...editForm, label: e.target.value})}
                      className="bg-gray-50 border rounded-lg px-2 py-1 text-sm font-bold w-full"
                    />
                    <textarea 
                      value={editForm.question} 
                      onChange={e => setEditForm({...editForm, question: e.target.value})}
                      className="bg-gray-50 border rounded-lg px-2 py-1 text-xs w-full h-12"
                    />
                    <label className="flex items-center gap-2 cursor-pointer mt-1">
                       <input 
                         type="checkbox" 
                         checked={editForm.is_base} 
                         onChange={e => setEditForm({...editForm, is_base: e.target.checked})} 
                       />
                       <span className="text-xs font-bold text-gray-600">Toujours afficher (Base)</span>
                    </label>
                    {!editForm.is_base && (
                      <input 
                        value={(editForm as any).category_codes_str} 
                        onChange={e => setEditForm({...editForm, category_codes_str: e.target.value} as any)}
                        placeholder="Codes (03F, AE...)"
                        className="bg-gray-50 border rounded-lg px-2 py-1 text-xs w-full"
                      />
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-gray-800 text-sm truncate">{item.label}</h4>
                      {item.is_base ? (
                        <span className="bg-green-100 text-green-700 text-[10px] uppercase px-1.5 rounded-full font-bold">Base</span>
                      ) : (
                        <span className="bg-blue-100 text-blue-700 text-[10px] uppercase px-1.5 rounded-full font-bold">
                          {item.category_codes.length} cat.
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-1">{item.question}</p>
                    {!item.is_base && item.category_codes.length > 0 && (
                      <p className="text-[10px] text-gray-400 font-mono mt-1">
                        [{item.category_codes.join(', ')}]
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-1 ml-2">
                {isEditing ? (
                  <>
                    <button onClick={saveEdit} className="p-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition">
                      <Check size={18} />
                    </button>
                    <button onClick={() => setEditingId(null)} className="p-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition">
                      <X size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(item)} className="p-1.5 text-gray-400 hover:text-indigo-600 transition">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => { if(confirm('Sûr ?')) removeItem(item.id) }} className="p-1.5 text-gray-400 hover:text-red-500 transition">
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
